/**
 * Piano — a virtual keyboard, 1-3 octaves. Sends Note On / Note Off on
 * a fixed MIDI channel with a fixed velocity. Multi-touch: each pointer
 * plays its own note; overlapping fingers = polyphony.
 *
 * White keys share the item's width equally. Black keys overlay the
 * whites at the classic piano gaps between:
 *   C-D, D-E (gap after E), F-G, G-A, A-B (gap after B)
 *
 * Note-number math uses Ableton's convention where `rootOctave: 3`
 * places middle C (note 60) at the leftmost C. Formula:
 *   note = (rootOctave + 2) * 12 + semitone_from_C
 * so `rootOctave=3, semitone=0` = 60; `rootOctave=3, semitone=12` = 72
 * (start of next octave).
 *
 * Touch modes:
 *   momentary — press = Note On, release = Note Off (default)
 *   toggle    — press = Note On, second press = Note Off
 *   trigger   — press = Note On + immediate Note Off
 *
 * In edit mode all pointer events yield to the wrapper's drag arbiter;
 * no dispatch happens.
 */

import { createSignal, For, type Accessor } from 'solid-js';
import { fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { ulid } from '../utils/ulid.ts';
import { editOwnsGesture, isEditMode, type PianoItem, type LayoutColor } from '../layout/store.ts';
import type { MidiNoteStep } from '@opbuttons/schema';

interface PianoProps {
  item: PianoItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Semitone offsets of black keys from the C of an octave. */
const BLACK_SEMITONES = [1, 3, 6, 8, 10];

/** Given a black-key semitone, which white-key index (0..6) does it sit
 *  BETWEEN? Black at semitone 1 (C#) sits between whites 0 (C) and 1 (D). */
const BLACK_LEFT_WHITE: Record<number, number> = {
  1: 0,  // C# between C and D
  3: 1,  // D# between D and E
  6: 3,  // F# between F and G
  8: 4,  // G# between G and A
  10: 5, // A# between A and B
};

export function Piano(props: PianoProps) {
  // Per-note active set. Note number → count of active pointers on that
  // note (multi-touch: two fingers on the same key hold it, releasing
  // one keeps it playing). In toggle mode this becomes 0/1 latched.
  const [activeNotes, setActiveNotes] = createSignal<Map<number, number>>(new Map());

  function tokenFor(): string {
    return COLOR_TOKEN[props.item.color];
  }

  function whiteCount(): number {
    return props.item.octaves * 7;
  }

  function noteFor(octaveIndex: number, semitone: number): number {
    return (props.item.rootOctave + 2) * 12 + octaveIndex * 12 + semitone;
  }

  function isActive(note: number): boolean {
    return (activeNotes().get(note) ?? 0) > 0;
  }

  function sendNoteOn(note: number): void {
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      const step: MidiNoteStep = {
        type: 'midi.note',
        id: ulid(),
        target,
        channel: props.item.channel + 1,
        note,
        velocity: props.item.velocity,
        duration: 0,
      };
      void fireAction(step);
    }
  }

  function sendNoteOff(note: number): void {
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      const step: MidiNoteStep = {
        type: 'midi.note',
        id: ulid(),
        target,
        channel: props.item.channel + 1,
        note,
        velocity: 0,
        duration: 0,
      };
      void fireAction(step);
    }
  }

  function activate(note: number): void {
    setActiveNotes((prev) => {
      const next = new Map(prev);
      next.set(note, (next.get(note) ?? 0) + 1);
      return next;
    });
  }

  function deactivate(note: number): void {
    setActiveNotes((prev) => {
      const next = new Map(prev);
      const c = (next.get(note) ?? 0) - 1;
      if (c <= 0) next.delete(note);
      else next.set(note, c);
      return next;
    });
  }

  function pressKey(note: number): void {
    if (editOwnsGesture(props.item.id)) return;
    const mode = props.item.mode ?? 'momentary';
    if (mode === 'trigger') {
      sendNoteOn(note);
      sendNoteOff(note);
      // Flash the active state briefly so the tap is visible.
      activate(note);
      setTimeout(() => deactivate(note), 120);
      return;
    }
    if (mode === 'toggle') {
      if (isActive(note)) {
        // Second press releases the held note. Force count to 0.
        setActiveNotes((prev) => {
          const next = new Map(prev);
          next.delete(note);
          return next;
        });
        sendNoteOff(note);
      } else {
        activate(note);
        sendNoteOn(note);
      }
      return;
    }
    // momentary
    activate(note);
    sendNoteOn(note);
  }

  function releaseKey(note: number): void {
    if (editOwnsGesture(props.item.id)) return;
    const mode = props.item.mode ?? 'momentary';
    if (mode === 'trigger' || mode === 'toggle') return; // release is a no-op
    if (isActive(note)) {
      deactivate(note);
      if (!isActive(note)) sendNoteOff(note);
    }
  }

  function onKeyPointerDown(e: PointerEvent & { currentTarget: HTMLDivElement }, note: number) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pressKey(note);
  }

  function onKeyPointerUp(e: PointerEvent & { currentTarget: HTMLDivElement }, note: number) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ }
    releaseKey(note);
  }

  return (
    <div class="piano" data-token={tokenFor()} data-mode={props.item.mode ?? 'momentary'}>
      <div class="piano-keys">
        {/* White keys — flex-share the width. */}
        <For each={Array.from({ length: props.item.octaves })}>
          {(_o, oIdx) => (
            <For each={[0, 2, 4, 5, 7, 9, 11]}>
              {(semitone, wIdx) => {
                const note = noteFor(oIdx(), semitone);
                const isC = semitone === 0;
                return (
                  <div
                    class="piano-white-key"
                    data-note={note}
                    data-active={isActive(note) ? 'true' : 'false'}
                    onPointerDown={(e) => onKeyPointerDown(e, note)}
                    onPointerUp={(e) => onKeyPointerUp(e, note)}
                    onPointerCancel={(e) => onKeyPointerUp(e, note)}
                    onPointerLeave={(e) => onKeyPointerUp(e, note)}
                  >
                    {isC ? (
                      <span class="piano-white-label">
                        C{props.item.rootOctave + oIdx()}
                      </span>
                    ) : null}
                    {/* Ignore wIdx to avoid unused-var lint. */}
                    <span class="piano-white-index" data-idx={wIdx()} />
                  </div>
                );
              }}
            </For>
          )}
        </For>

        {/* Black keys — absolute positioned over the white key rail. */}
        <For each={Array.from({ length: props.item.octaves })}>
          {(_o, oIdx) => (
            <For each={BLACK_SEMITONES}>
              {(semitone) => {
                const note = noteFor(oIdx(), semitone);
                const leftWhiteInOctave = BLACK_LEFT_WHITE[semitone]!;
                const whiteIdxAcrossKeyboard = oIdx() * 7 + leftWhiteInOctave;
                const total = whiteCount();
                // Black key horizontally centered on the boundary between
                // the left white and the next white. Width = ~60% of a
                // white key.
                const leftPct = ((whiteIdxAcrossKeyboard + 1) / total) * 100;
                const widthPct = (0.6 / total) * 100;
                return (
                  <div
                    class="piano-black-key"
                    data-note={note}
                    data-active={isActive(note) ? 'true' : 'false'}
                    style={{
                      left: `calc(${leftPct}% - ${widthPct / 2}%)`,
                      width: `${widthPct}%`,
                    }}
                    onPointerDown={(e) => onKeyPointerDown(e, note)}
                    onPointerUp={(e) => onKeyPointerUp(e, note)}
                    onPointerCancel={(e) => onKeyPointerUp(e, note)}
                    onPointerLeave={(e) => onKeyPointerUp(e, note)}
                  />
                );
              }}
            </For>
          )}
        </For>
      </div>
      <div class="piano-readout">
        <span>{props.item.octaves} oct · root C{props.item.rootOctave}</span>
        <span class="piano-readout-mode">{props.item.mode ?? 'momentary'}</span>
        <span>ch {props.item.channel + 1} · vel {props.item.velocity}</span>
      </div>
    </div>
  );
}
